import { useState } from 'react';
import { ROUTES } from '@/constants/routes';
import {
  BankApplicationListItem,
  BankApplicationStatus,
} from '@/types/bankApplications';
import { formatDateTimeFromUTC } from '@/utils/date';
import { useNavigate } from 'react-router-dom';
import { SVListItemCard } from '../core';
import BankApplicationDeleteDialog from './BankApplicationDeleteDialog';
import {
  getBankAccountApplicationTitle,
  getBankApplicationAccountTypeLabel,
  getBankApplicationAdvisorText,
  getThirdPartyProcessorStatusString,
} from '@/utils/bankAccountApplications';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import BankApplicationVerifyExternalAccountsDialog from './BankApplicationVerifyExternalAccountsDialog';

type BankApplicationsListProps = {
  bankApplications: BankApplicationListItem[];
};

export default function BankApplicationsList({
  bankApplications,
}: BankApplicationsListProps) {
  const navigate = useNavigate();
  const { isStaff } = useAuthUser();
  const [openDelete, setOpenDelete] = useState(false);
  const [openVerify, setOpenVerify] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<{
    uuid: string;
    applicationId: string;
    ownerName: string;
  } | null>(null);

  const handleOpenModal = (
    application: BankApplicationListItem,
    isDelete: boolean,
  ) => {
    const { primaryOwner, business } = application;

    setSelectedApplication({
      uuid: application.uuid,
      applicationId: application.applicationId,
      ownerName: getBankAccountApplicationTitle({
        fullName: primaryOwner ? primaryOwner.fullName : '',
        email: primaryOwner ? primaryOwner.email : '',
        businessName: business ? business.name : '',
      }),
    });
    if (isDelete) {
      setOpenDelete(true);
    } else {
      setOpenVerify(true);
    }
  };

  return (
    <>
      {bankApplications.map(application => {
        const {
          uuid,
          applicationId,
          accountType,
          updatedAt,
          advisor,
          primaryOwner,
          business,
          status,
          thirdPartyProcessorStatus,
        } = application;
        const baseProps = {
          onClick: () =>
            navigate(
              `${ROUTES.BANK_ACCOUNT_APPLICATION_DETAILS.replace(':uuid', uuid)}`,
            ),
          header: getBankAccountApplicationTitle({
            fullName: primaryOwner ? primaryOwner.fullName : '',
            email: primaryOwner ? primaryOwner.email : '',
            businessName: business ? business.name : '',
          }),
          additionalInfo: [
            {
              label: 'Application ID',
              text: applicationId,
            },
            {
              label: 'Account Type',
              text: getBankApplicationAccountTypeLabel(accountType),
            },
            {
              label: 'Advisor',
              text: getBankApplicationAdvisorText(application.advisor),
            },
            {
              label: 'Company',
              text: advisor?.company?.name || 'N/A',
            },
            {
              label: 'Last Updated',
              text: formatDateTimeFromUTC(updatedAt, true, 'MM/DD/YYYY h:mm A'),
            },
          ],
        };

        if (
          isStaff &&
          (status === BankApplicationStatus.NEED_REVIEW ||
            status === BankApplicationStatus.AWAITING_E_SIGN ||
            status === BankApplicationStatus.SUBMITTED ||
            status === BankApplicationStatus.COMPLETED)
        ) {
          baseProps.additionalInfo.push({
            label: 'Third Party Processor Status',
            text: getThirdPartyProcessorStatusString(thirdPartyProcessorStatus),
          });
        }

        const viewApplicationItem = {
          label: 'View Application',
          onClick: () =>
            navigate(`${ROUTES.BANK_ACCOUNT_APPLICATIONS}/${uuid}`),
        };
        const editApplicationItem = {
          label: 'Edit Application',
          onClick: () =>
            navigate(`${ROUTES.BANK_ACCOUNT_APPLICATIONS}/${uuid}/edit`),
        };
        const deleteApplicationItem = {
          label: 'Delete Application',
          onClick: () => handleOpenModal(application, true),
        };

        if (
          status === BankApplicationStatus.COMPLETED ||
          status === BankApplicationStatus.DECLINED
        ) {
          return (
            <SVListItemCard
              {...baseProps}
              key={uuid}
              manageMenuItems={[viewApplicationItem]}
              status={status}
              statusType="bankAccountApplication"
            />
          );
        }

        if (status === BankApplicationStatus.DRAFT) {
          return (
            <SVListItemCard
              {...baseProps}
              key={uuid}
              manageMenuItems={[
                viewApplicationItem,
                editApplicationItem,
                deleteApplicationItem,
              ]}
            />
          );
        }

        if (
          isStaff &&
          status === BankApplicationStatus.EXTERNAL_ACCOUNT_REVIEW
        ) {
          return (
            <SVListItemCard
              {...baseProps}
              key={uuid}
              manageMenuItems={[
                viewApplicationItem,
                editApplicationItem,
                {
                  label: 'Verify External Accounts',
                  onClick: () => handleOpenModal(application, false),
                },
                deleteApplicationItem,
              ]}
            />
          );
        }

        return (
          <SVListItemCard
            {...baseProps}
            key={uuid}
            manageMenuItems={[viewApplicationItem, deleteApplicationItem]}
          />
        );
      })}
      {selectedApplication && (
        <>
          <BankApplicationDeleteDialog
            uuid={selectedApplication.uuid}
            applicationId={selectedApplication.applicationId}
            ownerName={selectedApplication.ownerName}
            open={openDelete}
            setOpen={setOpenDelete}
          />
          <BankApplicationVerifyExternalAccountsDialog
            uuid={selectedApplication.uuid}
            applicationId={selectedApplication.applicationId}
            ownerName={selectedApplication.ownerName}
            open={openVerify}
            setOpen={setOpenVerify}
          />
        </>
      )}
    </>
  );
}
